import type { Country, Hotel, Resort } from '@/shared/model';
import { emptySelection, isCountry, isHotels, isResorts, selectCountry, type Selection } from './selection';
import type { SelectionChip } from './types';
import { buildLabel } from '../lib/buildLabel';

export function toggleAllResortsMobile(current: Selection, country: Country, resorts: Resort[] = []): Selection {
  if (isCountry(current) && current.country.id === country.id) {
    return current;
  }
  return selectCountry(country, resorts);
}

export function toggleResortMobile(current: Selection, resort: Resort, country: Country): Selection {
  if (isResorts(current) && current.country.id === resort.countryId) {
    const alreadySelected = current.resorts.some((item) => item.id === resort.id);
    if (alreadySelected) {
      const remaining = current.resorts.filter((item) => item.id !== resort.id);
      if (remaining.length === 0) {
        return selectCountry(current.country, current.country.resorts ?? []);
      }
      return { kind: 'resorts', country: current.country, resorts: remaining };
    }
    return { kind: 'resorts', country: current.country, resorts: [...current.resorts, resort] };
  }

  const base = isCountry(current) && current.country.id === resort.countryId ? current.country : country;
  return { kind: 'resorts', country: { ...base, resorts: base.resorts ?? [] }, resorts: [resort] };
}

export function toggleHotelMobile(current: Selection, hotel: Hotel, country: Country): Selection {
  if (isHotels(current) && current.country.id === hotel.countryId) {
    const alreadySelected = current.hotels.some((item) => item.id === hotel.id);
    if (alreadySelected) {
      const remaining = current.hotels.filter((item) => item.id !== hotel.id);
      if (remaining.length === 0) {
        return selectCountry(current.country, current.country.resorts ?? []);
      }
      return { kind: 'hotels', country: current.country, hotels: remaining };
    }
    return { kind: 'hotels', country: current.country, hotels: [...current.hotels, hotel] };
  }

  return { kind: 'hotels', country: { ...country, resorts: country.resorts ?? [] }, hotels: [hotel] };
}

export function selectionCount(selection: Selection | null | undefined): number {
  if (!selection || selection.kind === 'none') {
    return 0;
  }
  if (selection.kind === 'resorts') {
    return selection.resorts.length;
  }
  if (selection.kind === 'hotels') {
    return selection.hotels.length;
  }
  return 1;
}

export function selectionChips(selection: Selection | null | undefined): SelectionChip[] {
  if (!selection || selection.kind === 'none') {
    return [];
  }
  if (selection.kind === 'resorts') {
    return selection.resorts.map((resort) => ({ type: 'resort' as const, id: resort.id, name: resort.name }));
  }
  if (selection.kind === 'hotels') {
    return selection.hotels.map((hotel) => ({ type: 'hotel' as const, id: hotel.id, name: hotel.name }));
  }
  return [{ type: 'country', id: selection.country.id, name: buildLabel(selection) }];
}

export function removeSelectionItem(selection: Selection, chip: SelectionChip): Selection {
  if (selection.kind === 'none') {
    return selection;
  }
  if (chip.type === 'country') {
    return isCountry(selection) && selection.country.id === chip.id ? emptySelection() : selection;
  }
  if (chip.type === 'resort' && isResorts(selection)) {
    const remaining = selection.resorts.filter((item) => item.id !== chip.id);
    if (remaining.length === selection.resorts.length) {
      return selection;
    }
    return remaining.length === 0
      ? selectCountry(selection.country, selection.country.resorts ?? [])
      : { ...selection, resorts: remaining };
  }
  if (chip.type === 'hotel' && isHotels(selection)) {
    const remaining = selection.hotels.filter((item) => item.id !== chip.id);
    if (remaining.length === selection.hotels.length) {
      return selection;
    }
    return remaining.length === 0
      ? selectCountry(selection.country, selection.country.resorts ?? [])
      : { ...selection, hotels: remaining };
  }
  return selection;
}
